import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Code, Database, Server, GitBranch, Plus, Search, Trash2, Eye, ArrowLeft, Loader2, CheckCircle, Settings, Layers } from 'lucide-react';
import { getStackProfiles, createStackProfile, deleteStackProfile, detectStackFromRepo, getStackProfileDashboard, getLanguages, getFrameworks, getDatabases, getMessageBuses, getDeployments, LANGUAGE_COLORS, DATABASE_ICONS, DEPLOYMENT_COLORS } from '@/services/stackProfile';
import { toast } from 'sonner';

const StackProfilesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine base path from current location
  const basePath = location.pathname.startsWith('/litesabre') && !location.pathname.startsWith('/litesabre-pro') 
    ? '/litesabre' 
    : '/grc/litesabre';
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetectModal, setShowDetectModal] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [detectionResult, setDetectionResult] = useState(null);
  
  // Options from API
  const [languageOptions, setLanguageOptions] = useState([]);
  const [frameworkOptions, setFrameworkOptions] = useState([]);
  const [databaseOptions, setDatabaseOptions] = useState([]);
  const [messageBusOptions, setMessageBusOptions] = useState([]);
  const [deploymentOptions, setDeploymentOptions] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    language: '',
    framework: '',
    databases: [],
    message_buses: [],
    deployment_type: '',
    security_targets: ['web_app', 'api']
  });
  
  const [repoData, setRepoData] = useState({
    repo_url: '',
    branch: 'main',
    name: ''
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
    loadOptions();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profilesData, dashboardData] = await Promise.all([
        getStackProfiles(),
        getStackProfileDashboard()
      ]);
      setProfiles(profilesData);
      setDashboard(dashboardData);
    } catch (error) {
      toast.error('Failed to load stack profiles');
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      console.log('Loading stack profile options...');
      const [langs, fws, dbs, buses, deploys] = await Promise.all([
        getLanguages(),
        getFrameworks(),
        getDatabases(),
        getMessageBuses(),
        getDeployments()
      ]);
      console.log('Languages loaded:', langs);
      setLanguageOptions(langs.languages || []);
      setFrameworkOptions(fws.frameworks || []);
      setDatabaseOptions(dbs.databases || []);
      setMessageBusOptions(buses.message_buses || []);
      setDeploymentOptions(deploys.deployments || []);
    } catch (error) {
      console.error('Failed to load options:', error);
      toast.error('Failed to load options');
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.language) {
      toast.error('Name and language are required');
      return;
    }
    
    try {
      setCreating(true);
      const payload = {
        name: formData.name,
        language: formData.language,
        framework: formData.framework || null,
        databases: formData.databases.map(d => ({ type: d })),
        message_buses: formData.message_buses.map(m => ({ type: m })),
        deployment: formData.deployment_type ? { type: formData.deployment_type } : null,
        security_targets: formData.security_targets
      };
      
      await createStackProfile(payload);
      toast.success('Stack profile created');
      setShowCreateModal(false);
      setFormData({
        name: '',
        language: '',
        framework: '',
        databases: [],
        message_buses: [],
        deployment_type: '',
        security_targets: ['web_app', 'api']
      });
      await loadData();
    } catch (error) {
      toast.error('Failed to create stack profile');
    } finally {
      setCreating(false);
    }
  };

  const handleDetect = async () => {
    if (!repoData.repo_url) {
      toast.error('Repository URL is required');
      return;
    }
    
    try {
      setDetecting(true);
      setDetectionResult(null);
      const result = await detectStackFromRepo(repoData);
      setDetectionResult(result);
      toast.success('Stack detected successfully');
      await loadData();
    } catch (error) {
      toast.error('Failed to detect stack from repository');
    } finally {
      setDetecting(false);
    }
  };

  const handleDelete = async (profileId) => {
    if (!confirm('Are you sure you want to delete this profile?')) return;
    
    try {
      await deleteStackProfile(profileId);
      toast.success('Profile deleted');
      await loadData();
    } catch (error) {
      toast.error('Failed to delete profile');
    }
  };

  const getLanguageStyle = (lang) => {
    return LANGUAGE_COLORS[lang] || LANGUAGE_COLORS.other;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`${basePath}`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Layers className="w-7 h-7 text-indigo-600" />
              Stack Profiles
            </h1>
            <p className="text-gray-500">Define and auto-detect application technology stacks</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowDetectModal(true)}>
            <GitBranch className="w-4 h-4 mr-2" />
            Detect from Repo
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Profile
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <p className="text-indigo-100 text-sm">Total Profiles</p>
              <p className="text-3xl font-bold">{dashboard.total_profiles}</p>
              <div className="flex gap-2 mt-2 text-sm">
                <span>Auto: {dashboard.auto_detected_count}</span>
                <span>Manual: {dashboard.manual_count}</span>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-gray-500 text-sm mb-2">By Language</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(dashboard.profiles_by_language || {}).map(([lang, count]) => (
                  <Badge key={lang} className={`${getLanguageStyle(lang).badge} text-white`}>
                    {lang}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-gray-500 text-sm mb-2">By Database</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(dashboard.profiles_by_database || {}).map(([db, count]) => (
                  <Badge key={db} variant="outline">
                    {DATABASE_ICONS[db] || '💾'} {db}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-gray-500 text-sm mb-2">By Deployment</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(dashboard.profiles_by_deployment || {}).map(([dep, count]) => (
                  <Badge key={dep} className={`${DEPLOYMENT_COLORS[dep] || 'bg-gray-500'} text-white`}>
                    {dep}: {count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Profiles Table */}
      <Card>
        <CardHeader>
          <CardTitle>Stack Profiles</CardTitle>
          <CardDescription>Manage your application technology stack configurations</CardDescription>
        </CardHeader>
        <CardContent>
          {profiles.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No stack profiles yet</p>
              <p className="text-sm text-gray-400">Create a profile manually or detect from a repository</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Language</TableHead>
                  <TableHead>Framework</TableHead>
                  <TableHead>Databases</TableHead>
                  <TableHead>Deployment</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.map((profile) => (
                  <TableRow key={profile.id}>
                    <TableCell className="font-medium">{profile.name}</TableCell>
                    <TableCell>
                      <Badge className={`${getLanguageStyle(profile.language).badge} text-white`}>
                        {profile.language}
                      </Badge>
                    </TableCell>
                    <TableCell>{profile.framework || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {(profile.databases || []).map((db, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {DATABASE_ICONS[db.type] || '💾'} {db.type}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {profile.deployment ? (
                        <Badge className={`${DEPLOYMENT_COLORS[profile.deployment.type] || 'bg-gray-500'} text-white`}>
                          {profile.deployment.type}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={profile.input_mode === 'repo_auto' ? 'default' : 'outline'}>
                        {profile.input_mode === 'repo_auto' ? 'Auto' : 'Manual'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(profile.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Stack Profile</DialogTitle>
            <DialogDescription>Define your application's technology stack</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Profile Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="My API Service"
              />
            </div>
            <div>
              <Label>Language</Label>
              <Select value={formData.language} onValueChange={(v) => setFormData({ ...formData, language: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {languageOptions.map((l) => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Framework</Label>
              <Select value={formData.framework} onValueChange={(v) => setFormData({ ...formData, framework: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select framework (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {frameworkOptions.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Deployment Type</Label>
              <Select value={formData.deployment_type} onValueChange={(v) => setFormData({ ...formData, deployment_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select deployment (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {deploymentOptions.map((d) => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detect Modal */}
      <Dialog open={showDetectModal} onOpenChange={setShowDetectModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detect Stack from Repository</DialogTitle>
            <DialogDescription>Auto-detect technology stack from your codebase</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Repository URL</Label>
              <Input
                value={repoData.repo_url}
                onChange={(e) => setRepoData({ ...repoData, repo_url: e.target.value })}
                placeholder="https://github.com/org/repo"
              />
            </div>
            <div>
              <Label>Branch</Label>
              <Input
                value={repoData.branch}
                onChange={(e) => setRepoData({ ...repoData, branch: e.target.value })}
                placeholder="main"
              />
            </div>
            <div>
              <Label>Profile Name (optional)</Label>
              <Input
                value={repoData.name}
                onChange={(e) => setRepoData({ ...repoData, name: e.target.value })}
                placeholder="Auto-generated if empty"
              />
            </div>
            
            {detectionResult && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Stack Detected</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">Language:</span> {detectionResult.detection_summary?.language}</div>
                    <div><span className="text-gray-500">Framework:</span> {detectionResult.detection_summary?.framework || 'N/A'}</div>
                    <div><span className="text-gray-500">Databases:</span> {detectionResult.detection_summary?.databases?.join(', ') || 'None'}</div>
                    <div><span className="text-gray-500">Deployment:</span> {detectionResult.detection_summary?.deployment || 'N/A'}</div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    {detectionResult.rule_packs?.total_rules} security rules selected
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetectModal(false)}>Close</Button>
            <Button onClick={handleDetect} disabled={detecting} className="bg-indigo-600 hover:bg-indigo-700">
              {detecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitBranch className="w-4 h-4 mr-2" />}
              {detecting ? 'Detecting...' : 'Detect Stack'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StackProfilesPage;
